import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, CircularProgress,
  Alert, Tabs, Tab, InputAdornment, IconButton,
} from "@mui/material";

import { useAuth } from "./AuthContext";

const EyeIcon = ({ off }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {off
      ? <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
      : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
    }
  </svg>
);

const PHP_URL = process.env.REACT_APP_PHP_API_URL || "";

const C = {
  navy:  "#0B1F3A",
  red:   "#FF0A4B",
  white: "#FFFFFF",
  bg:    "#F5F7FA",
  muted: "#8899aa",
  border:"#dde3ec",
};

const Field = ({ label, value, onChange, type = "text", endAdornment, autoComplete, inputProps, name }) => (
  <TextField
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    type={type}
    name={name}
    fullWidth
    size="small"
    autoComplete={autoComplete}
    inputProps={inputProps}
    InputProps={endAdornment ? { endAdornment } : undefined}
    sx={{
      mb: 1.5,
      "& .MuiOutlinedInput-root": {
        borderRadius: 1,
        "& fieldset": { borderColor: C.border },
        "&:hover fieldset": { borderColor: C.navy },
        "&.Mui-focused fieldset": { borderColor: C.navy },
      },
      "& .MuiInputLabel-root.Mui-focused": { color: C.navy },
    }}
  />
);

const SubmitBtn = ({ loading, label, onClick, disabled }) => (
  <Button
    fullWidth
    variant="contained"
    onClick={onClick}
    disabled={loading || disabled}
    sx={{
      mt: 0.5,
      bgcolor: C.navy,
      color: C.white,
      fontWeight: 700,
      textTransform: "none",
      borderRadius: 1,
      py: 1.1,
      "&:hover": { bgcolor: "#162d52" },
      "&:disabled": { bgcolor: C.muted },
    }}
  >
    {loading ? <CircularProgress size={18} sx={{ color: C.white }} /> : label}
  </Button>
);

// ── Email + Password tab ───────────────────────────────────────────────────────
const EmailPassForm = ({ onSuccess, onError }) => {
  const { login } = useAuth();
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  const submit = async () => {
    setError("");
    if (!email || !password) { setError("Email and password are required"); return; }
    setLoading(true);
    try {
      await login({ email, password });
      onSuccess?.();
    } catch (e) {
      setError(e.message);
      onError?.(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{error}</Alert>}
      <Field label="Email" value={email} onChange={setEmail} type="email" name="email" autoComplete="email" />
      <Field
        label="Password" value={password} onChange={setPassword}
        type={showPass ? "text" : "password"} name="password" autoComplete="current-password"
        endAdornment={
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShowPass(p => !p)}>
              <EyeIcon off={showPass} />
            </IconButton>
          </InputAdornment>
        }
      />
      <SubmitBtn loading={loading} label="Sign in" onClick={submit} />
    </Box>
  );
};

// ── Phone tab (Password or OTP) ────────────────────────────────────────────────
const PhoneForm = ({ onSuccess, onError }) => {
  const { login } = useAuth();
  const [mode,    setMode]    = useState("pass"); // "pass" | "otp"
  const [phone,   setPhone]   = useState("");
  const [password,setPassword]= useState("");
  const [otp,     setOtp]     = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [showPass,setShowPass]= useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const sendOtp = async () => {
    setError("");
    if (!phone) { setError("Phone number required"); return; }
    setLoading(true);
    try {
      await fetch(`${PHP_URL}/auth/pin/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      setOtpSent(true);
    } catch {
      setError("Failed to send code. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    setError("");
    if (!phone) { setError("Phone number required"); return; }
    if (mode === "pass" && !password) { setError("Password required"); return; }
    if (mode === "otp"  && !otp)      { setError("Enter the code"); return; }
    setLoading(true);
    try {
      await login(
        mode === "pass"
          ? { phone, password }
          : { phone, pin: otp }
      );
      onSuccess?.();
    } catch (e) {
      setError(e.message);
      onError?.(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{error}</Alert>}
      <Field label="Phone number" value={phone} onChange={setPhone} type="tel" name="phone" autoComplete="tel" />

      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
        {["pass", "otp"].map((m) => (
          <Box
            key={m}
            onClick={() => { setMode(m); setError(""); setOtpSent(false); setOtp(""); }}
            sx={{
              fontSize: 12, fontWeight: 600, cursor: "pointer", px: 1.5, py: 0.5,
              borderRadius: 1, border: `1px solid ${mode === m ? C.navy : C.border}`,
              color: mode === m ? C.navy : C.muted,
              bgcolor: mode === m ? "#e8edf5" : "transparent",
            }}
          >
            {m === "pass" ? "Password" : "Text code"}
          </Box>
        ))}
      </Box>

      {mode === "pass" && (
        <Field
          label="Password" value={password} onChange={setPassword}
          type={showPass ? "text" : "password"} autoComplete="current-password"
          endAdornment={
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setShowPass(p => !p)}>
                <EyeIcon off={showPass} />
              </IconButton>
            </InputAdornment>
          }
        />
      )}

      {mode === "otp" && !otpSent && (
        <SubmitBtn loading={loading} label="Send code" onClick={sendOtp} />
      )}

      {mode === "otp" && otpSent && (
        <>
          <Alert severity="info" sx={{ mb: 1.5, fontSize: 12 }}>Code sent — check your texts</Alert>
          <Field label="6-digit code" value={otp} onChange={setOtp} type="text" inputProps={{ maxLength: 6, pattern: "[0-9]*" }} />
          <SubmitBtn loading={loading} label="Verify code" onClick={submit} />
          <Button
            fullWidth size="small"
            onClick={() => { setOtpSent(false); setOtp(""); }}
            sx={{ mt: 0.5, textTransform: "none", color: C.muted, fontSize: 12 }}
          >
            Resend code
          </Button>
        </>
      )}

      {mode === "pass" && (
        <SubmitBtn loading={loading} label="Sign in" onClick={submit} />
      )}
    </Box>
  );
};

// ── Magic Link tab ─────────────────────────────────────────────────────────────
const MagicLinkForm = () => {
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null); // null | { url: string }
  const [error,   setError]   = useState("");
  const [copied,  setCopied]  = useState(false);

  const send = async () => {
    setError("");
    if (!email) { setError("Email required"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${PHP_URL}/auth/magic/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      setResult({ url: data.url || "" }); // single atomic update — sent + url together
    } catch {
      setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    if (!result?.url) return;
    navigator.clipboard.writeText(result.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (result !== null) {
    const url = result.url;
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 2, fontSize: 12 }}>
          Link sent — check your email (valid 24 hours, single use)
        </Alert>
        {url && (
          <Box>
            <Box
              sx={{
                fontSize: 11, wordBreak: "break-all", bgcolor: C.bg,
                border: `1px solid ${C.border}`, borderRadius: 1, p: 1.5, mb: 1,
                color: C.muted, fontFamily: "monospace",
              }}
            >
              {url}
            </Box>
            <Button
              fullWidth variant="outlined" onClick={copy}
              sx={{ textTransform: "none", borderColor: C.navy, color: C.navy, fontWeight: 700 }}
            >
              {copied ? "Copied!" : "Copy link"}
            </Button>
          </Box>
        )}
        <Button
          fullWidth size="small"
          onClick={() => { setResult(null); setCopied(false); }}
          sx={{ mt: 1, textTransform: "none", color: C.muted, fontSize: 12 }}
        >
          Send another
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{error}</Alert>}
      <Typography sx={{ fontSize: 12, color: C.muted, mb: 1.5 }}>
        Enter your email and we'll generate a one-time login link.
      </Typography>
      <Field label="Email" value={email} onChange={setEmail} type="email" name="magic-email" autoComplete="email" />
      <SubmitBtn loading={loading} label="Generate link" onClick={send} />
    </Box>
  );
};

// ── Main LoginModal ────────────────────────────────────────────────────────────
export default function LoginModal({ onSuccess }) {
  const [tab, setTab] = useState(0);

  return (
    <Box
      sx={{
        position: "fixed", inset: 0, zIndex: 9999,
        bgcolor: "rgba(11,31,58,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <Box
        sx={{
          bgcolor: C.white, borderRadius: 2, width: "100%", maxWidth: 420,
          mx: 2, boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ bgcolor: C.navy, px: 3, py: 2.5 }}>
          <Box component="img" src="/brandWhitePNG.png" alt="Pollis" sx={{ height: 40, mb: 0.5, display: "block" }} />
          <Typography sx={{ color: "#5a7898", fontSize: 12, mt: 0.25 }}>
            Sign in to continue
          </Typography>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            borderBottom: `1px solid ${C.border}`,
            "& .MuiTab-root": { fontSize: 12, fontWeight: 700, textTransform: "none", minWidth: 0 },
            "& .Mui-selected": { color: `${C.navy} !important` },
            "& .MuiTabs-indicator": { bgcolor: C.navy },
          }}
        >
          <Tab label="Email" />
          <Tab label="Phone" />
          <Tab label="Magic link" />
        </Tabs>

        {/* Form */}
        <Box sx={{ p: 3 }}>
          {tab === 0 && <EmailPassForm onSuccess={onSuccess} />}
          {tab === 1 && <PhoneForm     onSuccess={onSuccess} />}
          {tab === 2 && <MagicLinkForm />}
        </Box>
      </Box>
    </Box>
  );
}
